import { forwardRef } from 'react';
import type { ForwardedRef, ForwardRefExoticComponent } from 'react';
import type {
  AllBadgeStatus,
  BadgeStatus,
  BaseBadgeProps,
} from '@components/Badge/BaseBadge';
import { BaseBadge } from '@components/Badge/BaseBadge';
import { useStrings } from '@hooks/useStrings';

export type BadgeProps<T extends AllBadgeStatus = AllBadgeStatus> = Omit<
  BaseBadgeProps<T>,
  'as' | 'ref'
>;

/**
 * This is a render for the badge.
 * We do this, so we can use TS generics, because forward ref doesn't
 * work well with them.
 */
const BadgeRender = <T extends AllBadgeStatus = 'custom'>(
  { kind, size = 'm', className, children, ...props }: BadgeProps<T>,
  ref: ForwardedRef<HTMLElement>,
): JSX.Element => {
  const { badge } = useStrings();
  const badgeKey = kind as BadgeStatus;
  const defaultBadgeText = badge[badgeKey];
  return (
    <BaseBadge
      {...props}
      size={size}
      kind={kind || 'custom'}
      className={className}
      ref={ref}
    >
      {kind === 'custom' ? children : defaultBadgeText}
    </BaseBadge>
  );
};

/**
 * Small labels that help categorize, or organize items using keywords.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/05c5b9-badge Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-badge--badge Code documentation}
 *
 * @example
 * <Badge kind='active' />
 */
export const Badge = forwardRef(
  BadgeRender,
) as ForwardRefExoticComponent<BadgeProps>;

Badge.displayName = 'Badge';
